/* ============================================================
   FreeLimitsBanner — owner directive: users sitting on FREE-tier
   limits (trial, canceled/never-paid, payment-issue) must SEE it
   and get a visible "Activate" CTA, wherever they land.
   Silent by design: any fetch failure or an exempt/genuinely-paid
   workspace renders nothing — this is a nudge, not a gate.
   ============================================================ */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Icon } from './Icons.jsx';
import { useDashboardData } from '../state/DashboardDataContext.jsx';
import PaymentIssueNotice from './PaymentIssueNotice.jsx';
import { isLapsedPaymentIssue } from '../lib/paymentIssue.js';

const DISMISS_KEY = 'dathent.freeLimitsBanner.dismissed';

function readDismissed() {
  try {
    return sessionStorage.getItem(DISMISS_KEY) === '1';
  } catch (_) {
    return false;
  }
}

function writeDismissed() {
  try {
    sessionStorage.setItem(DISMISS_KEY, '1');
  } catch (_) { /* private mode */ }
}

export default function FreeLimitsBanner({ compact = false }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { entitlements, accountId } = useDashboardData();
  const [dismissed, setDismissed] = useState(readDismissed);

  useEffect(() => {
    setDismissed(readDismissed());
  }, [accountId]);

  if (!entitlements) return null;
  if (entitlements?.billingGateExempt === true) return null;

  const { billingStatus, effectivePlan, paymentIssue } = entitlements || {};

  // TASK-331 (M6 fix): BillingStateBanner (mounted app-wide in App.jsx) already
  // renders its own "Your plan has lapsed" card + Renew CTA for
  // accessState === 'lapsed'. FreeLimitsBanner mounts on Dashboard/CreatePost/
  // PreviewContext too, so without this a lapsed org saw BOTH cards stacked
  // (two "Your plan has lapsed" banners, three CTAs total) — the review's M6
  // finding. Smaller-change choice: suppress ONLY this banner's lapsed
  // rendering; PaymentIssueNotice itself is untouched (Settings.jsx still uses
  // it directly for the billing-tab detail view) and the dunning (kind
  // 'dunning', i.e. grace) rendering here is unaffected.
  if (paymentIssue && !isLapsedPaymentIssue(paymentIssue)) {
    return <PaymentIssueNotice paymentIssue={paymentIssue} planName={entitlements?.planName || entitlements?.plan} graceEndsAt={entitlements?.accessState === 'grace' ? entitlements.graceEndsAt : null} compact={compact} />;
  }
  if (dismissed) return null;

  // Genuinely paid: not on free/trial tier and no open payment issue.
  const isPaid = effectivePlan !== 'free' && effectivePlan !== 'trial' && !paymentIssue;
  if (isPaid) return null;

  let variant = null;
  if (billingStatus === 'trialing') variant = 'trial';
  else if (effectivePlan === 'free') variant = 'free';
  if (!variant) return null;

  const message = variant === 'trial'
      ? t('freeLimits.trialMsg', { defaultValue: "You're on Free limits during your trial — full capabilities unlock after your first payment." })
      : t('freeLimits.freeMsg', { defaultValue: "You're on Free limits. Activate a plan to unlock full capabilities." });

  const ctaLabel = t('freeLimits.cta', { defaultValue: 'Activate' });

  const handleCta = () => {
    navigate('/app/plans');
  };

  const handleDismiss = () => {
    writeDismissed();
    setDismissed(true);
  };

  return (
    <section className={`card flb-banner${compact ? ' flb-compact' : ''}`} role="status">
      <span className="flb-ic">
        <Icon name="sparkles" size={compact ? 13 : 14} />
      </span>
      <p className="flb-msg">{message}</p>
      <div className="flb-actions">
        <button type="button" className="btn btn-primary btn-sm flb-cta" onClick={handleCta}>
          {ctaLabel}
        </button>
        <button type="button" className="flb-x" aria-label="Dismiss" onClick={handleDismiss}>
          <Icon name="x" size={13} />
        </button>
      </div>
    </section>
  );
}
